import type { ISerialize } from '@/objects/ISerialize';
import type { TransactionReason } from './../enums/TransactionReason';
import { formatDateIso } from '@/utilities/dateutils';

export class TimerecorderTIMMTransactionRequest implements ISerialize {
    public Bib: number | null;

    public ContestantID: number | null;

    public Disabled: boolean;

    public HighPriority: boolean;

    public Processed: boolean;

    public RaceContestantID: number | null;

    public Reason: string | null;

    public ReasonSource: TransactionReason | null;

    public SaveState: boolean | null;

    public SentToServer: Date | null;

    public StationID: number | null;

    public StationRoleID: number | null;

    public TransactionTime: Date | null;

    public TUID: string | null;

    /***
     * @deprecated Use the static FromJsValues method instead of the constructor to get type safety and default values
     */
    public constructor(values: {
        Bib?: number | null;
        ContestantID?: number | null;
        Disabled?: boolean;
        HighPriority?: boolean;
        Processed?: boolean;
        RaceContestantID?: number | null;
        Reason?: string | null;
        ReasonSource?: TransactionReason | null;
        SaveState?: boolean | null;
        SentToServer?: Date | null;
        StationID?: number | null;
        StationRoleID?: number | null;
        TransactionTime?: Date | null;
        TUID?: string | null;
    }) {
        this.Bib = values.Bib ?? null;

        this.ContestantID = values.ContestantID ?? null;

        this.Disabled = values.Disabled ?? false;

        this.HighPriority = values.HighPriority ?? false;

        this.Processed = values.Processed ?? false;

        this.RaceContestantID = values.RaceContestantID ?? null;

        this.Reason = values.Reason ?? null;

        this.ReasonSource = values.ReasonSource ?? 'HeatPlan';

        this.SaveState = values.SaveState ?? null;

        this.SentToServer = values.SentToServer ?? null;

        this.StationID = values.StationID ?? null;

        this.StationRoleID = values.StationRoleID ?? null;

        this.TransactionTime = values.TransactionTime ?? null;

        this.TUID = values.TUID ?? null;
    }

    public serialize(): string {
        return (
            '{' +
            `"Bib": ${JSON.stringify(this.Bib)}` +
            ',' +
            `"ContestantID": ${JSON.stringify(this.ContestantID)}` +
            ',' +
            `"Disabled": ${JSON.stringify(this.Disabled)}` +
            ',' +
            `"HighPriority": ${JSON.stringify(this.HighPriority)}` +
            ',' +
            `"Processed": ${JSON.stringify(this.Processed)}` +
            ',' +
            `"RaceContestantID": ${JSON.stringify(this.RaceContestantID)}` +
            ',' +
            `"Reason": ${JSON.stringify(this.Reason)}` +
            ',' +
            `"ReasonSource": ${this.ReasonSource == null ? null : this.ReasonSource == 'HeatPlan' ? 0 : this.ReasonSource == 'TimeRecorder' ? 1 : null}` +
            ',' +
            `"SaveState": ${JSON.stringify(this.SaveState)}` +
            ',' +
            `"SentToServer": ${this.SentToServer ? '"' + formatDateIso(this.SentToServer) + '"' : null}` +
            ',' +
            `"StationID": ${JSON.stringify(this.StationID)}` +
            ',' +
            `"StationRoleID": ${JSON.stringify(this.StationRoleID)}` +
            ',' +
            `"TransactionTime": ${this.TransactionTime ? '"' + formatDateIso(this.TransactionTime) + '"' : null}` +
            ',' +
            `"TUID": ${JSON.stringify(this.TUID)}` +
            '}'
        );
    }

    /**
     * Creates an instance of TimerecorderTIMMTransactionRequest from the supplied values.
     * Missing values have default values
     * @param values The values you want to set
     * @returns An object instance with the supplied values set
     */
    public static FromJsValues(values: {
        Bib?: number | null;
        ContestantID?: number | null;
        Disabled?: boolean;
        HighPriority?: boolean;
        Processed?: boolean;
        RaceContestantID?: number | null;
        Reason?: string | null;
        ReasonSource?: TransactionReason | null;
        SaveState?: boolean | null;
        SentToServer?: Date | null;
        StationID?: number | null;
        StationRoleID?: number | null;
        TransactionTime?: Date | null;
        TUID?: string | null;
    }): TimerecorderTIMMTransactionRequest {
        return new TimerecorderTIMMTransactionRequest(values);
    }

    public static _FromServerJson(json: any): TimerecorderTIMMTransactionRequest {
        return new TimerecorderTIMMTransactionRequest({
            Bib: json.Bib ?? null,
            ContestantID: json.ContestantID ?? null,
            Disabled: json.Disabled,
            HighPriority: json.HighPriority,
            Processed: json.Processed,
            RaceContestantID: json.RaceContestantID ?? null,
            Reason: json.Reason ?? '',
            ReasonSource:
                json.ReasonSource == null ? null : json.ReasonSource == 0 ? 'HeatPlan' : json.ReasonSource == 1 ? 'TimeRecorder' : null,
            SaveState: json.SaveState ?? null,
            SentToServer: json.SentToServer ? (json.SentToServer instanceof Date ? json.SentToServer : new Date(json.SentToServer)) : null,
            StationID: json.StationID ?? null,
            StationRoleID: json.StationRoleID ?? null,
            TransactionTime: json.TransactionTime
                ? json.TransactionTime instanceof Date
                    ? json.TransactionTime
                    : new Date(json.TransactionTime)
                : null,
            TUID: json.TUID ?? '',
        });
    }

    /**
     * Creates an instance of TimerecorderTIMMTransactionRequest from another instance of TimerecorderTIMMTransactionRequest.
     * @param other The TimerecorderTIMMTransactionRequest you want to clone
     * @returns A cloned TimerecorderTIMMTransactionRequest
     */
    public static FromOther(other: TimerecorderTIMMTransactionRequest): TimerecorderTIMMTransactionRequest {
        return TimerecorderTIMMTransactionRequest._FromServerJson(JSON.parse(other.serialize()));
    }

    /**
     * Deepclones the object.
     */
    public DeepClone() {
        return TimerecorderTIMMTransactionRequest.FromOther(this);
    }

    static NAME_OF_CLASS = 'TimerecorderTIMMTransactionRequest';
}
